import useSWR from "swr";
import { api } from "services/api.service";

export interface SupportArticle {
  id: string;
  title: string;
  content: string;
  category: string;
  tags: string[];
  lastModified: string;
  url?: string;
  slug?: string;
  summary?: string;
}

interface NotionPageProperties {
  [key: string]: any;
}

interface NotionPage {
  id: string;
  properties: NotionPageProperties;
  url: string;
  lastEditedTime: string;
}

interface NotionResponse {
  results: NotionPage[];
}

const NOTION_API_URL = "/notion";

const extractText = (richText: any): string => {
  if (!richText || !Array.isArray(richText)) return "";
  return richText.map((text: any) => text.plainText || "").join("");
};

const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
};

const transformNotionPageToArticle = (page: NotionPage): SupportArticle | null => {
  const properties = page.properties;

  const title = extractText(properties.name?.title);
  const summary = extractText(properties.summary?.richText);
  const category = extractText(properties.category?.richText);
  const topics = extractText(properties.topics?.richText);
  const slug = extractText(properties.slug?.richText);
  const status = extractText(properties.status?.richText);

  if (status?.toLowerCase() === "draft") {
    return null;
  }

  return {
    id: page.id,
    title: title || "Untitled",
    content: summary || "",
    summary: summary || "",
    category: category || "General",
    tags: topics
      ? topics
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean)
      : [],
    lastModified: page.lastEditedTime,
    url: page.url,
    slug: slug || generateSlug(title),
  };
};

export const fetchFromNotion = async (): Promise<SupportArticle[]> => {
  const response = await api.post(`${NOTION_API_URL}/databases/query`, {});

  const notionData: NotionResponse = response.data;
  const articles = notionData.results
    .map(transformNotionPageToArticle)
    .filter((article): article is SupportArticle => article !== null);

  return articles;
};

export function useSupportArticles() {
  const { data, error, isLoading, mutate } = useSWR<SupportArticle[]>("notion-support-articles", fetchFromNotion, {
    revalidateOnFocus: false,
    dedupingInterval: 5 * 60 * 1000,
    errorRetryCount: 2,
  });

  return {
    articles: data ?? [],
    isLoading,
    isError: Boolean(error),
    error,
    refresh: mutate,
  };
}
